import { useEffect, useRef, useState } from 'react';
import { isTweening, retarget, stillTween, tweenAt, type Tween } from '@casa/util';
import { useAppForeground, useScreenActive } from './screen_activity.ts';

// Numbers that move to a new value instead of jumping to it (docs/specs/gauge-styles.md §7).

export interface Tweened {
    /** The numbers to draw at `now` (the frame's time, `performance.now()` clock). */
    at: (now: number) => (number | null)[];
    /** Whether they are moving: draw on every frame (`CanvasView`'s `animate`) while they are. */
    isMoving: boolean;
}

/** Whether the system asks for reduced motion. */
export const prefersReducedMotion = () : boolean => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * `values`, moved to over `duration` ms each time one changes, from where they are shown at that
 * moment. They jump instead on a screen that is not shown, in the background, and where the system
 * asks for reduced motion.
 */
export function useTween(values: readonly (number | null)[], duration: number) : Tweened {
    const isShown = useScreenActive() && useAppForeground();
    const tween = useRef<Tween>(stillTween(values));
    const now = performance.now();
    // Started while rendering, so that the first frame drawn with the new values already moves; the
    // same values keep the tween, so rendering twice does not restart it.
    tween.current = retarget(tween.current, values, now, isShown && !prefersReducedMotion() ? duration : 0);
    const current = tween.current;
    const isMoving = isTweening(current, now);

    // Rendered again when the move is over, to stop drawing on every frame.
    const [, setEnded] = useState(0);
    useEffect(() => {
        if (!isMoving) {
            return undefined;
        }

        const timer = setTimeout(() => setEnded((count) => count + 1), Math.max(0, current.start + current.duration - performance.now()) + 20);
        return () => clearTimeout(timer);
    }, [current, isMoving]);

    return { at: (frameTime) => tweenAt(current, frameTime), isMoving };
}
